import React, { useState, useEffect, useCallback } from 'react';
import { Bot, Building2, ArrowLeftRight, Waves, Landmark, ChevronDown, CalendarDays } from 'lucide-react';
import FedChip from './FedChip';
import MacroPanel from './MacroPanel';
import { useWS } from '../WSContext';

const MAIN_WS = window.location.port === '3000' || window.location.port === '3001'
  ? `ws://${window.location.hostname}:8866`
  : 'wss://ws.barbarosquant.org/ws';

const ORDERLY_WS = window.location.port === '3000' || window.location.port === '3001'
  ? `ws://${window.location.hostname}:8876`
  : 'wss://ws.barbarosquant.org/sol-ws';

const DEEPBLUE_WS = window.location.port === '3000' || window.location.port === '3001'
  ? `ws://${window.location.hostname}:8879`
  : 'wss://ws.barbarosquant.org/deepblue-ws';

const NAVI_WS = window.location.port === '3000' || window.location.port === '3001'
  ? `ws://${window.location.hostname}:8884`
  : 'wss://ws.barbarosquant.org/navi-ws';

const DYDXBF_BOT   = { id: 'dydx-bf',   name: 'dYdX × Bluefin',  wsUrl: '', network: 'arb' };
const DYDXRAY_BOT  = { id: 'dydx-ray',  name: 'dYdX × Raydium', wsUrl: '', network: 'arb' };
const DYDXAPEX_BOT = { id: 'dydx-apex', name: 'dYdX × Apex Pro', wsUrl: '', network: 'arb' };

const DYDX_WS = window.location.port === '3000' || window.location.port === '3001'
  ? `ws://${window.location.hostname}:8887`
  : 'wss://ws.barbarosquant.org/dydx-ws';

const RAYDIUM_SUB_BOT    = { id: 'Raydium - Sub',      name: 'Raydium - Int Arb',  wsUrl: MAIN_WS,         network: 'solana' };
const SOL_TIMUR_BOT      = { id: 'Sol - Timur Bey',   name: 'Sol - Timur Bey',    wsUrl: MAIN_WS,         network: 'solana' };
const BLUEFIN_BOT        = { id: 'Bluefin - Backpack', name: 'Bluefin - Backpack', wsUrl: MAIN_WS,         network: 'sui'    };
const PHANTOM_BOT        = { id: 'Bluefin - Phantom',  name: 'Bluefin - Phantom',  wsUrl: MAIN_WS,         network: 'sui'    };
const VERA_BOT           = { id: 'vera',               name: 'Vera Wallet',        wsUrl: MAIN_WS,         network: 'sui'    };
const ORDERLY_BOT        = { id: 'orderly-main',      name: 'Timur Bey',          wsUrl: ORDERLY_WS,      network: 'solana' };
const DEEPBLUE_BOT       = { id: 'deepblue-main',     name: 'DeepBlue',           wsUrl: DEEPBLUE_WS,     network: 'sui'    };
// Hyperliquid ANA backend'e taşındı: eskiden 'hyperliquid-main' ayrı bir sunucuya
// (hl-ws) bağlanıyordu ama ikisi de aynı hl-node köprüsüne, yani aynı cüzdana
// bakıyordu. Kutucuk duruyor, işaret ettiği yer değişti.
const HYPERLIQUID_BOT    = { id: 'Hyperliquid',       name: 'Hyperliquid',        wsUrl: MAIN_WS,         network: 'evm'    };
const DYDX_BOT           = { id: 'dydx-main',         name: 'dYdX Perps',         wsUrl: DYDX_WS,         network: 'cosmos' };
const BINANCE_ARB_BOT    = { id: 'binance-arb',       name: 'Binance Arb',     wsUrl: '',              network: 'cex'    };
const KUCOIN_FUNDING_BOT = { id: 'kucoin-funding',    name: 'KuCoin Funding',  wsUrl: '',              network: 'cex'    };
const DECIBEL_TRADE_BOT  = { id: 'decibel-trade',     name: 'Decibel Trade',   wsUrl: '',              network: 'aptos'  };
const APEX_BOT_BOT       = { id: 'apex-bot',          name: 'Apex Pro',        wsUrl: '',              network: 'stark'  };
const NAVI_LIQ_BOT       = { id: 'navi-liquidator',   name: 'Navi Liquidator', wsUrl: NAVI_WS,         network: 'sui-liq' };
const ASTER_BOT          = { id: 'aster1',            name: 'AsterDex',        wsUrl: MAIN_WS,         network: 'evm'    };
const BINANCE_FUT_BOT    = { id: 'binance1',          name: 'Binance Futures', wsUrl: MAIN_WS,         network: 'binance' };
// Bu liste bots.json'dan TÜREMİYOR, elle tutuluyor — yeni bot eklerken buraya da
// yazmak gerekiyor, yoksa backend'de çalışsa bile bu ekranda görünmüyor.
const EXTENDED_BOT       = { id: 'Extended',           name: 'Extended',        wsUrl: MAIN_WS,         network: 'stark'  };
const BLUEFIN_LP_BOT     = { id: 'Bluefin - LP',      name: 'Bluefin - LP',    wsUrl: MAIN_WS,         network: 'sui'    };
// Bu ikisi listede HİÇ YOKTU; aktif kullanılıyor olmalarına rağmen seçim ekranından
// erişilemiyordu. `network` bilerek 'cex'/'binance' DEĞİL: o değerler kartı ikon ve
// renk olarak CEX tarayıcısı gibi gösteriyor ve equity satırını gizliyor.
const KUCOIN_BOT         = { id: 'KuCoin',             name: 'KuCoin',          wsUrl: MAIN_WS,         network: 'kucoin' };
const LIGHTER_BOT        = { id: 'lighter',            name: 'Lighter',         wsUrl: MAIN_WS,         network: 'zk'     };

interface Props {
  username: string;
  onSelect: (botId: string, wsEndpoint?: string) => void;
}

function SmallLogo() {
  return (
    <svg width="40" height="40" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon points="60,4 50,28 70,28" fill="#4A9FE8"/>
      <polygon points="26,18 38,42 52,30" fill="#4A9FE8" opacity="0.8"/>
      <polygon points="94,18 82,42 68,30" fill="#4A9FE8" opacity="0.8"/>
      <circle cx="60" cy="72" r="42" fill="#003087"/>
      <path d="M 20 60 Q 60 44 100 60" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 20 80 Q 60 96 100 80" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 60 30 Q 44 60 60 114" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M 60 30 Q 76 60 60 114" stroke="#4A9FE8" strokeWidth="2.5" fill="none" opacity="0.6"/>
      <circle cx="60" cy="72" r="18" fill="#4A9FE8" opacity="0.15"/>
    </svg>
  );
}

// Solana logosu (mor/yeşil gradient daire)
function SolanaIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="sol-g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#9945FF"/>
          <stop offset="100%" stopColor="#14F195"/>
        </linearGradient>
      </defs>
      <rect width="24" height="24" rx="6" fill="url(#sol-g)"/>
      <path d="M6 16.5h10l-2-3H6v3zm0-5.5h8l2-3H6v3zm0-5.5h10l-2 3H6V5.5z" fill="white"/>
    </svg>
  );
}

// Sui logosu (mavi daire)
function SuiIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="6" fill="#4DA2FF"/>
      <path d="M12 4C9 4 7 6.5 7 9c0 2 1 3.5 2.5 4.5L12 20l2.5-6.5C16 12.5 17 11 17 9c0-2.5-2-5-5-5zm0 7a2 2 0 110-4 2 2 0 010 4z" fill="white"/>
    </svg>
  );
}

// Cosmos / dYdX ikonu
function CosmosIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="6" fill="#6F4CFF"/>
      <circle cx="12" cy="12" r="3" fill="white"/>
      <ellipse cx="12" cy="12" rx="8" ry="3.5" stroke="white" strokeWidth="1.2" fill="none"/>
      <ellipse cx="12" cy="12" rx="8" ry="3.5" stroke="white" strokeWidth="1.2" fill="none" transform="rotate(60 12 12)"/>
      <ellipse cx="12" cy="12" rx="8" ry="3.5" stroke="white" strokeWidth="1.2" fill="none" transform="rotate(120 12 12)"/>
    </svg>
  );
}

// EVM / Hyperliquid ikonu
function EvmIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="6" fill="#00B4E6"/>
      <path d="M12 3L4 9.5l8 4.5 8-4.5L12 3z" fill="white" opacity="0.9"/>
      <path d="M4 13.5L12 18l8-4.5" stroke="white" strokeWidth="1.5" fill="none"/>
    </svg>
  );
}

// Aptos logosu (mavi/kırmızı)
// Starknet — Extended ve Apex Pro bu ağda
function StarknetIcon() {
  return (
    <span className="inline-flex items-center justify-center w-4 h-4 rounded-[4px]"
      style={{ background: 'linear-gradient(135deg,#0C0C4F,#28286E)' }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
        <path d="M12 2l2.6 6.1L21 10l-5 4.2L17.4 21 12 17.6 6.6 21 8 14.2 3 10l6.4-1.9L12 2z"
          fill="#EC796B" />
      </svg>
    </span>
  );
}

function AptosIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="6" fill="#2D3CF1"/>
      <path d="M17 7h-2.5l-2 2.5-2-2.5H8l4 5-4 5h2.5l2-2.5 2 2.5H17l-4-5 4-5z" fill="white"/>
    </svg>
  );
}

// Binance CEX ikonu
function BinanceIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="6" fill="#F0B90B"/>
      <path d="M12 4l2.5 2.5-6.5 6.5-2.5-2.5L12 4zM6.5 9.5L9 12l-2.5 2.5L4 12l2.5-2.5zm11 0L20 12l-2.5 2.5L15 12l2.5-2.5zM12 20l-2.5-2.5 6.5-6.5 2.5 2.5L12 20zm0-5l-2.5-2.5 2.5-2.5 2.5 2.5L12 15z" fill="white"/>
    </svg>
  );
}

function BotButton({ bot, state, onClick }: {
  bot: any;
  state: any;
  onClick: () => void;
}) {
  const account  = state?.account;
  const posCount = state?.positions?.length ?? 0;
  const isCex     = bot.network === 'cex' || bot.network === 'aptos' || bot.network === 'sui-liq';
  const isArb     = bot.network === 'arb';
  const isBinance = bot.network === 'binance';
  const isChain   = !isCex && !isArb;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left border rounded-xl p-4 transition-all group ${
        isArb
          ? 'bg-[#001a1a]/60 hover:bg-[#002a2a] border-[#00b4e6]/30 hover:border-[#00b4e6]'
          : isCex
          ? 'bg-[#1a1200]/60 hover:bg-[#2a1e00] border-[#F0B90B]/30 hover:border-[#F0B90B]'
          : 'bg-[#0a2560]/50 hover:bg-[#0a2560] border-[#003087] hover:border-[#4A9FE8]'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
            isArb
              ? 'bg-[#00b4e6]/10 group-hover:bg-[#00b4e6]/20'
              : isCex || isBinance
              ? 'bg-[#F0B90B]/10 group-hover:bg-[#F0B90B]/20'
              : 'bg-[#003087]/50 group-hover:bg-[#003087]'
          }`}>
            {isArb
              ? <ArrowLeftRight size={16} className="text-[#00b4e6]" />
              : isBinance
              ? <BinanceIcon />
              : isCex
              ? <Building2 size={16} className="text-[#F0B90B]" />
              : <Bot size={16} className="text-[#4A9FE8]" />
            }
          </div>
          <div>
            <p className="text-sm font-bold text-white">{bot.name}</p>
            <p className="text-[11px] text-blue-400 mt-0.5">
              {isArb
                ? bot.id === 'dydx-ray'
                  ? 'dYdX × Orderly (Raydium Perps) Funding Arb'
                  : 'Cross-DEX Funding Arbitrage Scanner'
                : isBinance
                ? posCount > 0 ? `${posCount} açık pozisyon` : 'Binance USDⓈ-M Futures'
                : isCex
                ? bot.id === 'kucoin-funding'
                  ? 'Futures Funding Rate Tarayıcı'
                  : bot.id === 'decibel-trade'
                    ? 'Aptos L1 On-Chain Perps DEX'
                    : bot.id === 'navi-liquidator'
                      ? 'Sui Navi Lending Liquidation Sniper'
                      : 'Spot + Perp Funding Arb'
                : bot.network === 'evm'
                  ? posCount > 0 ? `${posCount} açık pozisyon` : 'EVM L1 Perps DEX'
                  : posCount > 0 ? `${posCount} açık pozisyon` : 'Pozisyon yok'}
            </p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {account && !isCex && (
            <>
              <p className="text-sm font-bold text-[#4A9FE8]">${account.totalAccountValue?.toFixed(2)}</p>
              <p className={`text-[11px] ${account.crossUnrealizedPnl >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {account.crossUnrealizedPnl >= 0 ? '+' : ''}${account.crossUnrealizedPnl?.toFixed(2)} PnL
              </p>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

// 4 sütuna dengeli dağıtım: her sütun toplamda ~eşit yükseklikte
const COLUMNS: Array<Array<{ label: string; icon: React.ReactNode; bots: any[] }>> = [
  [
    { label: 'Solana',    icon: <SolanaIcon />,   bots: [SOL_TIMUR_BOT] },
    { label: 'Sui',       icon: <SuiIcon />,      bots: [BLUEFIN_BOT, BLUEFIN_LP_BOT] },
  ],
  [
    { label: 'EVM',       icon: <EvmIcon />,      bots: [HYPERLIQUID_BOT, ASTER_BOT] },
    { label: 'zk Rollup', icon: <EvmIcon />,      bots: [LIGHTER_BOT] },
  ],
  [
    { label: 'Starknet',  icon: <StarknetIcon />, bots: [EXTENDED_BOT] },
    { label: 'Cosmos',    icon: <CosmosIcon />,   bots: [DYDX_BOT] },
  ],
  [
    { label: 'CEX',       icon: <BinanceIcon />,  bots: [BINANCE_FUT_BOT, KUCOIN_BOT] },
  ],
];

// Kullanımdan düşmüş / yardımcı sayfalar. Silinmiyorlar — hâlâ erişilebilir olmaları
// gerekiyor — ama kapalı bir kartın içine alındılar ki ana ekran yalnız aktif
// kullanılan 10 botu göstersin. Üst şeritte (App.tsx) ise hiç görünmüyorlar.
const OTHER_GROUPS: Array<{ label: string; icon: React.ReactNode; bots: any[] }> = [
  { label: 'Solana',    icon: <SolanaIcon />,   bots: [RAYDIUM_SUB_BOT] },
  { label: 'Sui',       icon: <SuiIcon />,      bots: [PHANTOM_BOT, VERA_BOT, DEEPBLUE_BOT] },
  { label: 'Sui Liq',   icon: <SuiIcon />,      bots: [NAVI_LIQ_BOT] },
  { label: 'Aptos',     icon: <AptosIcon />,    bots: [DECIBEL_TRADE_BOT] },
  { label: 'Arbitraj',  icon: <EvmIcon />,      bots: [DYDXBF_BOT, DYDXRAY_BOT, DYDXAPEX_BOT] },
  { label: 'Tarayıcı',  icon: <BinanceIcon />,  bots: [BINANCE_ARB_BOT, KUCOIN_FUNDING_BOT] },
];

// ─── Funding Countdown ────────────────────────────────────────────────────────

const FUNDING_EXCHANGES = [
  { id: 'BN',   label: 'Binance',     color: '#F0B90B', interval: '8h'         },
  { id: 'HL',   label: 'Hyperliquid', color: '#00D4FF', interval: '1h'         },
  { id: 'dYdX', label: 'dYdX',        color: '#9A7BFF', interval: 'continuous' },
  { id: 'BF',   label: 'Bluefin',     color: '#3B82F6', interval: '1h'         },
  { id: 'KC',   label: 'KuCoin',      color: '#00A478', interval: '8h'         },
  { id: 'OR',   label: 'Orderly',     color: '#10B981', interval: '8h'         },
  { id: 'EX',   label: 'Extended',    color: '#EC796B', interval: '1h'         },
];

function getNext8h(): number {
  const now = new Date();
  now.setUTCMinutes(0, 0, 0);
  const h = now.getUTCHours();
  const next = Math.ceil((h + 0.001) / 8) * 8;
  now.setUTCHours(next);
  return now.getTime();
}

function getNext1h(): number {
  const now = new Date();
  now.setUTCMinutes(0, 0, 0);
  now.setUTCHours(now.getUTCHours() + 1);
  return now.getTime();
}

function fmtCountdown(ms: number): string {
  const total = Math.max(0, ms - Date.now());
  const h = Math.floor(total / 3_600_000);
  const m = Math.floor((total % 3_600_000) / 60_000);
  const s = Math.floor((total % 60_000) / 1_000);
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function FundingCountdown() {
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const next8h = getNext8h();
  const next1h = getNext1h();

  return (
    <div className="flex items-center gap-3 flex-wrap justify-center">
      <span className="text-[10px] text-blue-700 uppercase tracking-widest font-bold mr-1">
        Next Funding
      </span>
      {FUNDING_EXCHANGES.map(ex => {
        const isContinuous = ex.interval === 'continuous';
        const next = ex.interval === '1h' ? next1h : next8h;
        const remaining = isContinuous ? Infinity : Math.max(0, next - Date.now());
        const urgent = !isContinuous && remaining < 5 * 60_000;
        return (
          <div key={ex.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#061530] border border-[#0a2560]">
            <span className="text-[10px] font-bold" style={{ color: ex.color }}>{ex.id}</span>
            <span className={`font-mono text-xs font-semibold tabular-nums ${
              urgent ? 'text-orange-400 animate-pulse' : isContinuous ? 'text-blue-700' : 'text-blue-200'
            }`}>
              {isContinuous ? 'sürekli' : fmtCountdown(next)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function BotSelectOverlay({ username, onSelect }: Props) {
  const { botsState } = useWS() as any;
  // Piyasalar ve LP Takip sekmeleri 30/09/2026'da kaldırıldı (işlevini yitirmişti;
  // LP takibi artık /lptrax'te).
  const [tab, setTab] = useState<'bots' | 'macro'>('bots');
  // Kullanımdan düşmüş sayfalar varsayılan olarak KAPALI
  const [showOthers, setShowOthers] = useState(false);

  return (
    <div className="fixed inset-0 bg-[#020b18] flex flex-col z-50">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className="flex items-center gap-4 px-8 py-4 border-b border-[#0a2560]/50 flex-shrink-0">
        <SmallLogo />
        <div>
          <h1 className="text-base font-black tracking-widest text-white uppercase leading-none">Raydium</h1>
          <p className="text-xs text-blue-400 mt-0.5">
            Hoş geldin, <span className="text-white font-semibold">{username}</span>
            <span className="text-blue-700 mx-1.5">—</span>
            {tab === 'bots' ? 'hangi botu kullanmak istiyorsun?' : 'Fed fiyatlaması ve makro takvim'}
          </p>
          {/* Fed fiyatlaması — her sekmede görünür, tıklayınca Makro sekmesi.
              Başlık satırına değil buraya: orada funding sayaçlarını sıkıştırıyordu. */}
          <div className="mt-1.5">
            <FedChip onOpen={() => setTab('macro')} />
          </div>
        </div>

        {/* Ortada: funding geri sayımı */}
        <div className="flex-1 flex justify-center px-4">
          <FundingCountdown />
        </div>

        {/* Tab switcher */}
        <div className="flex items-center gap-2 bg-[#061530] rounded-xl p-1 border border-[#0a2560]">
          <button
            onClick={() => window.location.assign('/lptrax')}
            title="LP Trax — pozisyon bazlı LP takibi"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all bg-sky-600 text-white hover:bg-sky-500"
          >
            <Waves size={15} />
            LP Trax
          </button>
          <button
            onClick={() => setTab('bots')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === 'bots'
                ? 'bg-[#003087] text-white'
                : 'text-blue-400 hover:text-blue-200'
            }`}
          >
            <Bot size={15} />
            Botlar
          </button>
          <button
            onClick={() => setTab('macro')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              tab === 'macro'
                ? 'bg-[#003087] text-white'
                : 'text-blue-400 hover:text-blue-200'
            }`}
          >
            <Landmark size={15} />
            Makro
          </button>
          {/* Bütçe ayrı kök (/butce) → sekme değil, tam sayfa yönlendirme (LP Trax gibi) */}
          <button
            onClick={() => window.location.assign('/butce')}
            title="Bütçe — kişisel gelir/gider defteri"
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all bg-violet-600 text-white hover:bg-violet-500"
          >
            <CalendarDays size={15} />
            Bütçe
          </button>
        </div>
      </header>

      {/* ── Bot seçim grid ─────────────────────────────────────────────────── */}
      {tab === 'bots' && (
        <div className="flex-1 grid grid-cols-4 gap-5 p-6 min-h-0 overflow-auto">
          {COLUMNS.map((col, ci) => (
            <div key={ci} className="flex flex-col gap-5">
              {col.map(group => (
                <div key={group.label}>
                  <div className="flex items-center gap-2 mb-2.5 px-0.5">
                    {group.icon}
                    <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400">
                      {group.label}
                    </span>
                    <div className="flex-1 h-px bg-[#0a2560]" />
                  </div>
                  <div className="flex flex-col gap-2">
                    {group.bots.map((bot: any) => (
                      <BotButton
                        key={bot.id}
                        bot={bot}
                        state={botsState?.[bot.id]}
                        onClick={() => onSelect(bot.id, bot.wsUrl || undefined)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* ── Diğerleri (kapalı) ─────────────────────────────────────────────── */}
      {tab === 'bots' && (
        <div className="px-6 pb-6 shrink-0">
          <button
            onClick={() => setShowOthers(v => !v)}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#0a2560]
                       bg-[#061536]/60 hover:bg-[#0a2560]/60 transition-colors"
          >
            <ChevronDown
              size={15}
              className={`text-blue-400 transition-transform ${showOthers ? 'rotate-180' : ''}`}
            />
            <span className="text-[11px] font-bold uppercase tracking-widest text-blue-400">
              Diğerleri
            </span>
            <span className="text-[11px] text-blue-400/60">
              {OTHER_GROUPS.reduce((n, g) => n + g.bots.length, 0)} sayfa
            </span>
            <div className="flex-1 h-px bg-[#0a2560]" />
          </button>

          {showOthers && (
            <div className="grid grid-cols-4 gap-5 mt-4">
              {OTHER_GROUPS.map(group => (
                <div key={group.label}>
                  <div className="flex items-center gap-2 mb-2.5 px-0.5">
                    {group.icon}
                    <span className="text-[10px] font-bold uppercase tracking-widest text-blue-400">
                      {group.label}
                    </span>
                    <div className="flex-1 h-px bg-[#0a2560]" />
                  </div>
                  <div className="flex flex-col gap-2">
                    {group.bots.map((bot: any) => (
                      <BotButton
                        key={bot.id}
                        bot={bot}
                        state={botsState?.[bot.id]}
                        onClick={() => onSelect(bot.id, bot.wsUrl || undefined)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Makro (Fed fiyatlaması) ────────────────────────────────────────── */}
      {tab === 'macro' && <MacroPanel />}

    </div>
  );
}
